import { useMemo } from 'react';
import { useLeads } from '../../../hooks/useLeads';
import type { Lead, LeadStatus, LeadSource } from '../../../mocks/crm';
import LeadsKanban from './components/LeadsKanban';
import LeadsTable from './components/LeadsTable';
import LeadDetailModal from './components/LeadDetailModal';
import { agencyInfo } from '../../../mocks/dashboard';
import { useState } from 'react';

const brand = agencyInfo.brandColor;

const statuses: ('All' | LeadStatus)[] = ['All', 'New', 'Contacted', 'Quoted', 'Negotiating', 'Won', 'Lost'];
const sources: ('All' | LeadSource)[] = ['All', 'WhatsApp', 'Website', 'Phone', 'Email', 'Referral', 'Walk-in'];

export default function LeadsPage() {
  const { leads, loading, updateLeadStatus } = useLeads();
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'All' | LeadStatus>('All');
  const [filterSource, setFilterSource] = useState<'All' | LeadSource>('All');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  const stats = useMemo(() => {
    const total = leads.length;
    const won = leads.filter((l) => l.status === 'Won').length;
    const newCount = leads.filter((l) => l.status === 'New').length;
    const totalValue = leads.reduce((s, l) => s + l.budgetNum, 0);
    const convRate = total > 0 ? Math.round((won / total) * 100) : 0;
    return { total, won, newCount, totalValue, convRate };
  }, [leads]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const matchSearch =
        l.name.toLowerCase().includes(search.toLowerCase()) ||
        l.service.toLowerCase().includes(search.toLowerCase()) ||
        l.email.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'All' || l.status === filterStatus;
      const matchSource = filterSource === 'All' || l.source === filterSource;
      return matchSearch && matchStatus && matchSource;
    });
  }, [leads, search, filterStatus, filterSource]);

  const handleStatusChange = (leadId: string, newStatus: LeadStatus) => {
    updateLeadStatus(leadId, newStatus);
  };

  const handleLeadClick = (lead: Lead) => setSelectedLead(lead);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Page Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-white">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Leads Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Track and convert your travel leads</p>
          </div>
          <button
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-user-add-line" />
            Add Lead
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Total Leads', value: stats.total, icon: 'ri-user-line', color: '#0D9488' },
            { label: 'New Today', value: stats.newCount, icon: 'ri-user-add-line', color: '#7C3AED' },
            { label: 'Total Value', value: `AED ${(stats.totalValue / 1000).toFixed(0)}K`, icon: 'ri-money-dollar-circle-line', color: '#D97706' },
            { label: 'Conversion Rate', value: `${stats.convRate}%`, icon: 'ri-percent-line', color: '#059669' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${s.color}15` }}>
                <i className={`${s.icon} text-base`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-800 leading-tight">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="px-6 py-3.5 border-b border-gray-100 bg-white flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 bg-gray-50 placeholder-gray-400"
            style={{ '--tw-ring-color': brand } as React.CSSProperties}
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-all whitespace-nowrap"
              style={
                filterStatus === s
                  ? { backgroundColor: 'white', color: brand, fontWeight: 600 }
                  : { color: '#6B7280' }
              }
            >
              {s}
            </button>
          ))}
        </div>

        {/* Source Filter */}
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value as 'All' | LeadSource)}
          className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white text-gray-600 focus:outline-none cursor-pointer"
        >
          {sources.map((s) => (
            <option key={s} value={s}>{s === 'All' ? 'All Sources' : s}</option>
          ))}
        </select>

        {/* View Toggle */}
        <div className="flex items-center bg-gray-100 rounded-xl p-1 ml-auto">
          <button
            onClick={() => setView('kanban')}
            className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all"
            style={view === 'kanban' ? { backgroundColor: 'white', color: brand } : { color: '#9CA3AF' }}
            title="Kanban View"
          >
            <i className="ri-layout-column-line text-sm" />
          </button>
          <button
            onClick={() => setView('list')}
            className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all"
            style={view === 'list' ? { backgroundColor: 'white', color: brand } : { color: '#9CA3AF' }}
            title="List View"
          >
            <i className="ri-list-check-2 text-sm" />
          </button>
        </div>

        <p className="text-xs text-gray-400 whitespace-nowrap">{filtered.length} results</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {view === 'kanban' ? (
          <LeadsKanban
            leads={filtered}
            onLeadClick={handleLeadClick}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <LeadsTable leads={filtered} onLeadClick={handleLeadClick} />
        )}
      </div>

      {/* Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}
